import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

type Blocker = { code: string; message: string };

type Eligibility = {
  blockers: Blocker[];
  warnings: Blocker[];
  instrumentType: string;
  client: {
    id: string;
    name: string;
    email: string | null;
    portalEnabled: boolean;
  };
  document: { id: string; title: string; reviewStatus: string; mimeType: string };
};

const INSTRUMENTS = [
  { value: 'retainer', label: 'Retainer / fee agreement' },
  { value: 'engagement_letter', label: 'Engagement letter' },
  { value: 'hipaa_auth', label: 'HIPAA / medical authorization' },
  { value: 'limited_scope', label: 'Limited-scope engagement (attorney attest)' },
  { value: 'contingency_fee', label: 'Contingency fee (attorney attest)' },
];

export default function RequestSignatureDialog({
  open,
  documentId,
  onClose,
  onSent,
}: {
  open: boolean;
  documentId: string | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [instrumentType, setInstrumentType] = useState('retainer');
  const [message, setMessage] = useState('');
  const [attorneyAttestation, setAttorneyAttestation] = useState(false);
  const [attorneyOverrideJoint, setAttorneyOverrideJoint] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [invite, setInvite] = useState<{ inviteUrl?: string; portalLoginUrl?: string } | null>(null);

  const token = () => getAuthToken();

  const load = async (type = instrumentType) => {
    if (!documentId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        documentId,
        instrumentType: type,
        attorneyAttestation: String(attorneyAttestation),
        attorneyOverrideJoint: String(attorneyOverrideJoint),
      });
      const res = await fetch(`/api/signature-requests/eligibility?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Unable to check eligibility');
      setEligibility(body.data);
      if (body.data?.instrumentType) setInstrumentType(body.data.instrumentType);
    } catch (err) {
      toast.error(toUser(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && documentId) {
      setInvite(null);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId, attorneyAttestation, attorneyOverrideJoint]);

  const toUser = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

  const markReviewed = async () => {
    if (!documentId) return;
    try {
      const res = await fetch(`/api/documents/${documentId}/review-status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewStatus: 'attorney_reviewed' }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Could not mark attorney-reviewed'));
      toast.success('Marked attorney-reviewed');
      await load();
    } catch (err) {
      toast.error(toUser(err));
    }
  };

  const enablePortal = async () => {
    if (!eligibility?.client?.id) return;
    try {
      const res = await fetch(`/api/clients/${eligibility.client.id}/portal/enable`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Could not enable portal');
      setInvite({ inviteUrl: body.data?.inviteUrl, portalLoginUrl: body.data?.portalLoginUrl });
      toast.success('Portal invite created. Copy the link for the client, then refresh eligibility.');
      await load();
    } catch (err) {
      toast.error(toUser(err));
    }
  };

  const send = async () => {
    if (!documentId) return;
    setSending(true);
    try {
      const create = await fetch('/api/signature-requests', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          instrumentType,
          message,
          attorneyAttestation,
          attorneyOverrideJoint,
        }),
      });
      const created = await create.json();
      if (!create.ok) {
        throw new Error(created.message || 'Could not create signature request');
      }
      const sendRes = await fetch(`/api/signature-requests/${created.data.id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      });
      const sent = await sendRes.json();
      if (!sendRes.ok) throw new Error(sent.message || 'Could not send');
      toast.success('Signature request sent to the portal. Unsigned PDF was not emailed.');
      onSent();
      onClose();
    } catch (err) {
      toast.error(toUser(err));
    } finally {
      setSending(false);
    }
  };

  const blockers = eligibility?.blockers || [];
  const canSend = Boolean(eligibility) && blockers.length === 0 && !loading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request signature</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Native ArgisLegal e-sign (no DocuSign). Eligible: retainer, engagement letter, in-portal HIPAA
          authorization. Not for wills, notarized instruments, court originals, or settlement releases.
        </Typography>
        {eligibility && (
          <Alert severity={eligibility.client.portalEnabled ? 'info' : 'warning'} sx={{ mb: 2 }}>
            Signer: {eligibility.client.name} · {eligibility.client.email || 'no email'} · portal{' '}
            {eligibility.client.portalEnabled ? 'yes' : 'no'}
          </Alert>
        )}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Instrument</InputLabel>
          <Select
            label="Instrument"
            value={instrumentType}
            onChange={(e) => {
              const v = String(e.target.value);
              setInstrumentType(v);
              setTimeout(() => load(v), 0);
            }}
          >
            {INSTRUMENTS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Message to client (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={attorneyAttestation}
              onChange={(e) => setAttorneyAttestation(e.target.checked)}
            />
          }
          label="I am an attorney and attest this instrument is eligible for in-portal e-sign"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={attorneyOverrideJoint}
              onChange={(e) => setAttorneyOverrideJoint(e.target.checked)}
            />
          }
          label="Joint/co-plaintiff override: this envelope is for this client only"
        />
        {blockers.map((b) => (
          <Alert key={b.code} severity="error" sx={{ mt: 1 }}>
            {b.message}
            {b.code === 'PORTAL_USER_REQUIRED' && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={enablePortal}>
                  Enable portal / copy magic invite
                </Button>
              </Box>
            )}
            {b.code === 'ATTORNEY_REVIEW_REQUIRED' && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={markReviewed}>
                  Mark attorney-reviewed
                </Button>
              </Box>
            )}
          </Alert>
        ))}
        {(eligibility?.warnings || []).map((w) => (
          <Alert key={w.code} severity="warning" sx={{ mt: 1 }}>
            {w.message}
          </Alert>
        ))}
        {invite?.inviteUrl && (
          <Alert
            severity="success"
            sx={{ mt: 1 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  navigator.clipboard?.writeText(invite.inviteUrl || '');
                  toast.success('Invite link copied');
                }}
              >
                Copy
              </Button>
            }
          >
            Invite URL: {invite.inviteUrl}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSend || sending} onClick={send}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
